import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { formatList, pickupHubNames } from "#/lib/checkout/pickup";
import { pickupQueries } from "#/queries/pickup";
import { PillBtn } from "../components/primitives.js";

export const Route = createFileRoute("/shipping")({
  loader: ({ context: { queryClient } }) =>
    queryClient.ensureQueryData(pickupQueries.hubs()),
  head: () => ({ meta: [{ title: "Shipping" }] }),
  component: ShippingPage,
});

interface PartnerLogo {
  src: string;
  alt: string;
  className: string;
}

interface ShippingTierProps {
  heading: string;
  timeline: ReadonlyArray<string>;
  timelineNote?: string;
  trackingLabel?: string;
  partners: ReadonlyArray<PartnerLogo>;
  showTopDivider?: boolean;
}

function ShippingTier({
  heading,
  timeline,
  timelineNote,
  trackingLabel,
  partners,
  showTopDivider = true,
}: ShippingTierProps) {
  return (
    <section
      className={`py-16 md:py-20 ${showTopDivider ? "border-lp-green-mid/40 border-t" : ""}`}
    >
      <h3 className="m-0 text-center font-display font-semibold text-2xl text-lp-green-dk tracking-wider">
        {heading}
      </h3>
      <div className="mx-auto mt-8 max-w-xl text-center font-display text-base text-lp-text-dk leading-relaxed">
        {timeline.map((line) => (
          <div key={line}>{line}</div>
        ))}
        {timelineNote && (
          <p className="mx-auto mt-4 mb-0 max-w-md">{timelineNote}</p>
        )}
      </div>
      {trackingLabel && (
        <div className="mt-8 flex justify-center">
          <PillBtn variant="green" size="sm">
            {trackingLabel}
          </PillBtn>
        </div>
      )}
      <div className="mt-10 flex flex-wrap items-center justify-center gap-x-12 gap-y-6 md:gap-x-20">
        {partners.map((p) => (
          <img key={p.alt} src={p.src} alt={p.alt} className={p.className} />
        ))}
      </div>
    </section>
  );
}

function ShippingPage() {
  const { data: hubs = [] } = useQuery(pickupQueries.hubs());
  return (
    <div data-screen-label="Shipping & Delivery" className="pb-24">
      <section className="mx-auto w-full max-w-figma px-6 md:px-10 pt-16 md:pt-24">
        <h1 className="m-0 font-bold font-display text-4xl text-lp-green-dk leading-tight md:text-6xl">
          SHIPPING AND DELIVERY
        </h1>
        <p className="mt-4 font-display font-semibold text-lg text-lp-green-mid md:text-2xl">
          Track your order through our logistic's partner's website.
        </p>
      </section>

      <div className="mx-auto mt-10 w-full max-w-5xl px-6 md:px-10">
        <ShippingTier
          heading="METRO MANILA"
          timeline={["3 to 5 working days"]}
          timelineNote="Tracking details will be given to you after confirmed booking with our partner courier."
          partners={[
            {
              src: "/shippers/lalamove.png",
              alt: "Lalamove",
              className: "h-12 md:h-16 w-auto",
            },
          ]}
        />
        <ShippingTier
          heading="PROVINCIAL"
          timeline={[
            "Luzon – 5 to 7 working days",
            "Visayas – 8 to 10 working days",
            "Mindanao – 12 to 14 working days",
          ]}
          trackingLabel="LBC Tracking"
          partners={[
            {
              src: "/shippers/lbc.png",
              alt: "LBC – We Like To Move It",
              className: "h-28 md:h-36 w-auto",
            },
          ]}
        />
        <ShippingTier
          heading="INTERNATIONAL"
          timeline={["15 to 20 business days"]}
          partners={[
            {
              src: "/shippers/dhl.png",
              alt: "DHL",
              className: "h-12 md:h-16 w-auto",
            },
          ]}
        />
      </div>

      <div className="mt-16 w-full pr-6 md:pr-12">
        <div
          className="bg-lp-green-soft py-10 pr-10 pl-6 md:py-14 md:pr-32 md:pl-24"
          style={{
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            borderTopRightRadius: 300,
            borderBottomRightRadius: 300,
          }}
        >
          <h3 className="m-0 mb-5 font-display font-semibold text-xl text-lp-green-dk">
            Note:
          </h3>
          <ul className="m-0 list-none space-y-3 p-0 font-display text-base text-lp-text-dk leading-relaxed">
            <li>We will process your order within 1-2 working days.</li>
            <li>No deliveries during Sundays and Holidays</li>
            <li>
              Tracking details will be given to you after confirmed booking with
              our partner courier.
            </li>
            <li>Holiday surcharge may apply for Lalamove deliveries.</li>
            {hubs.length > 0 && (
              <li>
                Pick up is available in{" "}
                {formatList(pickupHubNames(hubs), "conjunction")}. See FAQs
                here.
              </li>
            )}
            <li>
              Please make sure to input the correct shipping details to avoid
              delays in order processing.
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
